import { designTokens } from '@app/theme';
import { useList } from '@refinedev/core';
import { formatDateTime, formatRelative } from '@shared/lib/format-date';
import { Card, Empty, Grid, Pagination, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AdminUserResponse } from '@api/generated/types.gen';

const { Title, Text } = Typography;

/**
 * Список пользователей платформы (`/api/admin/users`).
 *
 * Серверная пагинация через стандартный Spring `Pageable`. Refine `useList`
 * пробрасывает `pagination` в data-provider, который собирает `pageable.page/size/sort`.
 *
 * Каждый пользователь — вертикальная карточка-строка: «телефон + статус»,
 * роли, мета (ID, последний вход, создан). Работает и на десктопе,
 * и на мобильном (без таблицы); на узких экранах пагинация компактная.
 */
export function UsersListPage() {
  const { t } = useTranslation(['users', 'common']);
  const screens = Grid.useBreakpoint();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const { result, query } = useList<AdminUserResponse>({
    resource: 'users',
    pagination: { currentPage, pageSize },
  });

  const users = result.data;
  const total = result.total ?? 0;

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBlockEnd: 4, fontWeight: 600 }}>
          {t('users:title')}
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          {t('users:subtitle')}
        </Text>
      </div>

      <Card style={{ borderColor: designTokens.slate200 }} styles={{ body: { padding: 0 } }}>
        <Spin spinning={query.isLoading}>
          {!query.isLoading && users.length === 0 ? (
            <div style={{ padding: 32 }}>
              <Empty description={t('users:empty')} />
            </div>
          ) : (
            users.map((user, idx) => (
              <div
                key={user.id}
                style={{
                  padding: '12px 16px',
                  borderBlockStart: idx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <Link
                    to={`/users/${user.id ?? ''}`}
                    style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: 14,
                      fontWeight: 500,
                      color: designTokens.navy900,
                      borderBottom: `1px dashed ${designTokens.slate300}`,
                    }}
                  >
                    {user.phone}
                  </Link>
                  <Tag
                    color={user.enabled ? 'green' : 'red'}
                    style={{ fontSize: 11, fontWeight: 600, marginInlineEnd: 0 }}
                  >
                    {user.enabled ? t('users:status.enabled') : t('users:status.disabled')}
                  </Tag>
                </div>

                {(user.roles ?? []).length > 0 && (
                  <div style={{ marginBlockStart: 6 }}>
                    <Space wrap size={4}>
                      {(user.roles ?? []).map((role) => (
                        <Tag
                          key={role.id}
                          style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, margin: 0 }}
                        >
                          {role.name}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}

                <div
                  style={{
                    marginBlockStart: 6,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px 16px',
                  }}
                >
                  <Tooltip title={user.id}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace' }}
                    >
                      {t('users:columns.id')}: {(user.id ?? '').slice(0, 8)}…
                    </Text>
                  </Tooltip>
                  <Tooltip title={user.lastLoginAt ? formatDateTime(user.lastLoginAt) : undefined}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('users:columns.lastLoginAt')}:{' '}
                      {user.lastLoginAt ? formatRelative(user.lastLoginAt) : t('users:never')}
                    </Text>
                  </Tooltip>
                  <Tooltip title={formatDateTime(user.createdAt)}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('users:columns.createdAt')}: {formatRelative(user.createdAt)}
                    </Text>
                  </Tooltip>
                </div>
              </div>
            ))
          )}

          {total > pageSize && (
            <div
              style={{
                padding: '12px 16px',
                borderBlockStart: `1px solid ${designTokens.slate100}`,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                onChange={(p) => {
                  setCurrentPage(p);
                }}
                simple={!screens.md}
                showSizeChanger={false}
                showTotal={
                  screens.md
                    ? (t2, range) =>
                        t('users:pagination.total', { from: range[0], to: range[1], total: t2 })
                    : undefined
                }
              />
            </div>
          )}
        </Spin>
      </Card>
    </Space>
  );
}
